/**
 * The tab strip's render list (group chips + entries) and drag-and-drop drop resolution, over the shared shell model
 * (`Shell.tabUnits`). Web shell glue: hand-written, not translated.
 */
import { Shell, type ShellState, type ShellViewModel, type SplitSide, type TabGroup, type TabUnit } from "@bundle/core";

export type StripItem =
  | { kind: "unit"; key: string; unit: TabUnit; group: TabGroup | undefined; lastInGroup: boolean }
  | { kind: "group"; key: string; group: TabGroup; count: number };

/** Group chips followed by their entries (none while collapsed, except an entry on screen), lone entries, splits. */
export function stripItems(state: ShellState): StripItem[] {
  const groups = new Map(state.groups.map((g) => [g.id, g]));
  const units = Shell.tabUnits(state);
  const out: StripItem[] = [];
  const seen = new Set<string>();
  for (const u of units) {
    const g = u.groupId === undefined ? undefined : groups.get(u.groupId);
    if (g !== undefined && !seen.has(g.id)) {
      seen.add(g.id);
      out.push({ kind: "group", key: `group:${g.id}`, group: g, count: units.filter((x) => x.groupId === g.id).length });
    }
    if (g !== undefined && g.collapsed && !u.shown) continue;
    out.push({ kind: "unit", key: u.id, unit: u, group: g, lastInGroup: false });
  }
  for (let i = 0; i < out.length; i++) {
    const it = out[i]!;
    const next = out[i + 1];
    if (it.kind === "unit" && it.group !== undefined) it.lastInGroup = next === undefined || next.kind !== "unit" || next.group?.id !== it.group.id;
  }
  return out;
}

/** What is being dragged. `seg`: the segment of a split tab the drag started on (dropped on another one, they swap). */
export type DragSource = { kind: "unit"; key: string; unit: TabUnit; seg?: string } | { kind: "group"; key: string; group: TabGroup };

/** Where it would land. */
export type DropIntent =
  | { kind: "before" | "after"; key: string }
  /** Onto a tab: one split view. */
  | { kind: "combine"; key: string }
  /** Onto a group chip: the end of that group. */
  | { kind: "into"; key: string }
  /** After everything. */
  | { kind: "end" }
  /** Onto a pane's edge: split that pane. */
  | { kind: "pane"; paneId: string; side: SplitSide }
  /** One segment of a split tab onto another segment of the same tab: they swap sides. */
  | { kind: "swap"; key: string; a: string; b: string };

export function sameIntent(a: DropIntent | undefined, b: DropIntent | undefined): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** A lone tab can join another entry as a split view while that one has room. */
export function canCombine(state: ShellState, source: DragSource, target: TabUnit): boolean {
  return source.kind === "unit" && !source.unit.split && source.key !== target.id && target.tabIds.length < state.maxPanes && state.maxPanes > 1;
}

/** First index (in `tabs` without `skip`) of any of `ids`. */
function indexOf(state: ShellState, ids: string[], skip: Set<string>): number {
  const others = state.tabs.filter((t) => !skip.has(t.id));
  const i = others.findIndex((t) => ids.includes(t.id));
  return i < 0 ? others.length : i;
}

function afterLast(state: ShellState, ids: string[], skip: Set<string>): number {
  const others = state.tabs.filter((t) => !skip.has(t.id));
  let last = -1;
  others.forEach((t, i) => {
    if (ids.includes(t.id)) last = i;
  });
  return last + 1;
}

const groupOf = (it: StripItem | undefined): string | undefined => (it === undefined ? undefined : it.kind === "group" ? it.group.id : it.group?.id);

/** Performs a drop on the shell. */
export function applyDrop(shell: ShellViewModel, state: ShellState, items: StripItem[], source: DragSource, intent: DropIntent): void {
  if (intent.kind === "pane") {
    if (source.kind === "unit" && !source.unit.split) shell.splitPane(intent.paneId, source.unit.tabIds[0]!, intent.side);
    return;
  }
  if (intent.kind === "swap") {
    shell.swapPanes(intent.a, intent.b);
    return;
  }
  const target = intent.kind === "end" ? undefined : items.find((x) => x.key === intent.key);
  if (intent.kind === "combine") {
    if (source.kind === "unit" && target?.kind === "unit") shell.splitWith(target.unit.tabIds[0]!, [source.unit.tabIds[0]!]);
    return;
  }
  if (intent.kind === "into") {
    if (source.kind === "unit" && target?.kind === "group") shell.addToGroup(source.unit.tabIds, target.group.id);
    return;
  }
  // Neighbours of the drop point in the list without the dragged item(s).
  const moving = source.kind === "unit" ? new Set(source.unit.tabIds) : new Set(source.group.tabIds);
  const list = items.filter((x) => (source.kind === "unit" ? x.key !== source.key : x.key !== source.key && groupOf(x) !== source.group.id));
  let at = list.length;
  if (target !== undefined) {
    const i = list.findIndex((x) => x.key === target.key);
    if (i < 0) return;
    at = intent.kind === "before" ? i : i + 1;
  }
  const prev = list[at - 1];
  const next = list[at];
  const firstIds = (it: StripItem): string[] => (it.kind === "unit" ? it.unit.tabIds : it.group.tabIds);

  if (source.kind === "group") {
    // Never inside another group: after it.
    const g = groupOf(next);
    if (next !== undefined && next.kind === "unit" && g !== undefined && groupOf(prev) === g) {
      const other = state.groups.find((x) => x.id === g);
      shell.moveGroup(source.group.id, other === undefined ? indexOf(state, firstIds(next), moving) : afterLast(state, other.tabIds, moving));
    } else {
      shell.moveGroup(source.group.id, next === undefined ? state.tabs.length : indexOf(state, firstIds(next), moving));
    }
    return;
  }

  // Inside a group: between its chip / entries and one of its entries, or dropped on the right half of its last entry.
  let groupId: string | undefined;
  const ng = next?.kind === "unit" ? next.group : undefined;
  if (ng !== undefined && groupOf(prev) === ng.id) groupId = ng.id;
  else if (intent.kind === "after" && prev?.kind === "unit" && prev.group !== undefined && !prev.group.collapsed) groupId = prev.group.id;
  const index = next === undefined ? state.tabs.length : indexOf(state, firstIds(next), moving);
  shell.moveUnit(source.unit.tabIds[0]!, index, groupId);
}
